/**
 * ProcessingSplash — pantalla full-screen branded para mostrar mientras
 * esperamos que un proceso async termine de "asentarse" en backend.
 *
 * Casos típicos:
 *   - Post-Stripe Checkout: webhook async que crea la fila en
 *     `subscriptions` / `course_purchases` / `session_payments`.
 *   - Cualquier otro fire-and-forget donde el frontend redirige antes
 *     de que el backend termine (OAuth provisioning, enrolar a curso, etc).
 *
 * Uso:
 *   <ProcessingSplash
 *     title="Preparando tu entrada a Gurumba…"
 *     description="Tu pago fue recibido y estamos activando tu acceso…"
 *     hint="No cierres esta ventana."
 *   />
 *
 * Default copy está pensado para checkout de suscripción. Para otros
 * casos (compra de curso, booking) pasarle props específicos.
 */
import { cn } from '@/lib/utils';

interface ProcessingSplashProps {
  /** Headline grande. Default: "Preparando tu entrada a Gurumba…" */
  title?: string;
  /** Párrafo explicativo. Default: copy de activación post-pago. */
  description?: string;
  /** Texto chiquito al pie. Default: "No cierres esta ventana…" */
  hint?: string;
  className?: string;
}

export function ProcessingSplash({
  title = 'Preparando tu entrada a Gurumba…',
  description = 'Tu pago fue recibido y estamos activando tu acceso de forma segura. En unos segundos podrás entrar a tu clase, comunidad y beneficios.',
  hint = 'No cierres esta ventana. Estamos terminando la confirmación.',
  className,
}: ProcessingSplashProps) {
  return (
    <div
      className={cn(
        'flex min-h-screen items-center justify-center px-6 py-6',
        className,
      )}
      style={{
        background:
          'radial-gradient(circle at top, rgba(42,163,185,0.13) 0%, transparent 40%), linear-gradient(135deg, #0F2F3A 0%, #071B22 100%)',
      }}
    >
      <div className="w-full max-w-md rounded-3xl bg-white/[0.97] px-8 py-10 text-center shadow-elevated">
        {/* Orbit loader: aro dual-color que gira */}
        <div className="mx-auto mb-6 flex h-[72px] w-[72px] items-center justify-center">
          <div className="gurumba-orbit h-full w-full rounded-full" />
        </div>

        <h2 className="font-display text-2xl font-bold leading-tight text-brand-primary md:text-[26px]">
          {title}
        </h2>

        <p className="mx-auto mt-4 max-w-sm text-[15px] leading-relaxed text-[#3F4F55] md:text-base">
          {description}
        </p>

        {/* Barra de progreso indeterminada con gradient brand */}
        <div className="mt-7 mb-5 h-2 w-full overflow-hidden rounded-full bg-[#E8F3F5]">
          <div className="gurumba-progress-bar h-full rounded-full" />
        </div>

        <small className="block text-[13px] leading-snug text-[#6F7F84]">
          {hint}
        </small>
      </div>

      {/* Keyframes inline — evitamos tocar tailwind.config.js para no acoplar
          el resto del proyecto a estas animaciones. */}
      <style>{`
        .gurumba-orbit {
          border: 4px solid #E8F3F5;
          border-top-color: #2AA3B9;
          border-right-color: #8DA020;
          animation: gurumba-spin 1s linear infinite;
        }
        .gurumba-progress-bar {
          width: 45%;
          background: linear-gradient(90deg, #165A6E 0%, #2AA3B9 50%, #8DA020 100%);
          animation: gurumba-load 1.4s ease-in-out infinite;
        }
        @keyframes gurumba-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes gurumba-load {
          0%   { transform: translateX(-120%); }
          100% { transform: translateX(260%); }
        }
      `}</style>
    </div>
  );
}
